import { router } from "@inertiajs/react"
import { Mail, RotateCw, Trash2, UserPlus } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { StatusBadge } from "@/components/status-badge"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useOnline } from "@/hooks/use-online"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatRelativeIntl } from "@/lib/relative-time"
import { invitationPath, resendInvitationPath } from "@/routes"
import type { PendingFirstAdminInvite } from "@/types/admin"

interface PendingFirstAdminInviteCardProps {
  /**
   * Tenant context — drives empty-state copy (`Invite the first admin to
   * activate this {brand/agency}`) + the row's `data-tenant-id` attribute
   * for the QA `admin-pending-first-admin-card[data-tenant-id]` selector.
   */
  tenantId: number
  tenantKind: "brand" | "agency"
  /**
   * OKS-922 — WHO is looking, as opposed to WHAT they are looking at
   * (`tenantKind`). The two axes are orthogonal and both are needed: an
   * agency viewer on a brand tenant must be told the brand is waiting for
   * an admin WITHOUT being instructed to invite one, because the agency
   * surface no longer offers that action (see
   * `AgencyBrandsController#can_invite_first_admin?`).
   *
   * REQUIRED on purpose. An optional prop with a default would let a page
   * silently render admin copy to an agency viewer, and no Vitest fixture
   * could catch it — fixtures pass props explicitly, so they only ever
   * prove this component honours what it is GIVEN, never what the page
   * gives it. That is exactly the failure this ticket exists to fix
   * (`can_invite_first_admin` was correct in the component and wrong in
   * the derivation), and shipping it again inside the fix is not on.
   * Required makes the omission a compile error at every mount site.
   *
   * NB TypeScript catches an OMITTED prop, not a WRONG one — a page
   * passing "admin" when it means "agency" still compiles. That case is
   * covered by a rendered assertion in the system spec.
   */
  viewer: "admin" | "agency"
  /**
   * Pending or expired invite from `Invitation.live` (Backend Show scope).
   * `null` means no live invite — Card switches to empty-state branch.
   * Branch 1 of the TRD's 3-state matrix covers BOTH `"pending"` and
   * `"expired"` statuses (typo-recovery resend stays available; Resend
   * resets the clock per OKS-11 Rule 1).
   */
  invite: PendingFirstAdminInvite | null
  /**
   * Server-driven team count for the tenant. Branch gating:
   *   - `invite !== null`           → Card with Resend/Revoke (always shown)
   *   - `invite === null && teamSize === 0` → orphan EmptyState CTA
   *   - `invite === null && teamSize > 0`   → Card returns null (hidden)
   *
   * Branch 3 hidden case occurs when the first admin has already accepted
   * (their User row is in `team`) and no replacement invite is in flight.
   */
  teamSize: number
  /**
   * Slot rendered inside the orphan empty-state branch. Typically a
   * ready-to-mount `<InviteFirstAdminDialog>` (with its own
   * `<DialogTrigger asChild>` Button as the inner trigger). Pass
   * `undefined` (or omit) to suppress the CTA — Card renders the
   * orphan copy with no clickable affordance, suitable for cases where
   * the server says invitation is not currently allowed
   * (`can_invite_first_admin === false`).
   *
   * The slot pattern (vs an `onClick` callback) keeps the Card unaware
   * of which dialog opens — Brands/Show passes a brand-scoped instance,
   * Agencies/Show an agency-scoped instance, future surfaces can
   * compose differently without touching this component.
   */
  inviteAction?: ReactNode
}

/**
 * OKS-12 — Pending first-admin invite Card on Brand/Agency Show.
 *
 * Three render branches (TRD §State + UX Spec):
 *
 *   1. `invite !== null` (status pending OR expired) → metadata row + Resend
 *      ghost button + Revoke destructive ghost. Both `pending` and
 *      `expired` keep the Card visible — admin can resend a typo'd email
 *      after expiry; backend `Invitation.live` scope explicitly includes
 *      expired entries for this reason.
 *   2. `invite === null && teamSize === 0` → orphan EmptyState CTA (PRD
 *      AC7). Tenant exists but has neither admin nor pending invite —
 *      typically post-revoke. CTA renders via the `inviteAction` slot —
 *      typically a composed `<InviteFirstAdminDialog>` instance from the
 *      parent page (Brands/Show passes a brand-scoped instance,
 *      Agencies/Show an agency-scoped one). Slot omitted entirely when
 *      `can_invite_first_admin` is `false`, leaving the orphan copy
 *      without a clickable affordance.
 *   3. `invite === null && teamSize > 0` → return null. Admin already
 *      accepted; no replacement invite in flight; Team Card surfaces the
 *      seated admin elsewhere on the page.
 *
 * Resend is optimistic — toast fires immediately on click; if the
 * Inertia round-trip fails, an error toast surfaces and the page
 * re-fetches authoritative state on its own. Revoke uses an AlertDialog
 * with Cancel autoFocused per Crew destructive-dialog convention.
 *
 * Locked QA testid: `admin-pending-first-admin-card[data-tenant-id]` on
 * the Card region; `admin-pending-first-admin-resend` and
 * `admin-pending-first-admin-revoke-trigger` on the action buttons;
 * `admin-pending-first-admin-revoke-confirm` on the AlertDialog
 * destructive primary; `admin-pending-first-admin-empty-cta` on the
 * orphan EmptyState button.
 */
export function PendingFirstAdminInviteCard({
  tenantId,
  tenantKind,
  viewer,
  invite,
  teamSize,
  inviteAction,
}: PendingFirstAdminInviteCardProps) {
  const { t, i18n } = useTranslation()
  const online = useOnline()
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Branch 3 — Card hidden.
  if (!invite && teamSize > 0) return null

  // Branch 2 — orphan empty state.
  if (!invite) {
    return (
      <Card
        data-testid="admin-pending-first-admin-card"
        data-tenant-id={tenantId}
        data-card-state="empty"
      >
        <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
          <UserPlus
            className="text-muted-foreground size-8"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-1">
            <p className="text-foreground text-base font-medium">
              {t("admin.invites.card.empty.title")}
            </p>
            {/* Literal-arg `t()` per branch so the inertia_i18n health
             * scanner can statically prove every key is reachable.
             * Dynamic template strings silently bypass the scanner.
             *
             * OKS-922 — `viewer` is checked FIRST. An agency viewer only
             * ever reaches this card for a brand tenant (there is no
             * Agency/Agencies/Show), so the agency-viewer branch needs no
             * tenantKind split and no unreachable cell is constructed.
             * The admin branch falls through to the original ternary
             * unchanged, so both admin surfaces render exactly as before.
             *
             * The agency line deliberately states the fact and names the
             * route instead of instructing an action the viewer no longer
             * has — copy the admin line ("Invite the first admin…") would
             * be an instruction they cannot follow. */}
            <p className="text-muted-foreground text-sm">
              {viewer === "agency"
                ? t("admin.invites.card.empty.subtitle_brand_agency_viewer")
                : tenantKind === "brand"
                  ? t("admin.invites.card.empty.subtitle_brand")
                  : t("admin.invites.card.empty.subtitle_agency")}
            </p>
          </div>
          {/*
           * Slot — typically a `<InviteFirstAdminDialog>` whose own
           * `<DialogTrigger asChild>` button serves as the orphan CTA.
           * The button MUST carry `data-testid="admin-pending-first-
           * admin-empty-cta"` to satisfy the QA selector contract;
           * Brands/Show + Agencies/Show enforce this when composing.
           *
           * When omitted, the empty-state copy renders without a
           * clickable affordance — used for cases where the server says
           * inviting is not currently allowed.
           */}
          {inviteAction}
        </CardContent>
      </Card>
    )
  }

  // Branch 1 — pending or expired invite row.
  function handleResend() {
    if (!invite) return
    if (submitting || !online) return
    // Optimistic toast — surface success language immediately. If the
    // server-side Resend fails, the `onError` handler fires the rollback
    // error toast and the page re-fetches authoritative state on the
    // standard Inertia error path.
    toast.success(t("admin.invites.toast.resent"))
    setSubmitting(true)
    router.post(
      resendInvitationPath({ id: invite.id }),
      {},
      {
        preserveScroll: true,
        onError: () => {
          toast.error(t("admin.invites.errors.server"))
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleRevoke() {
    if (submitting || !online) return
    setSubmitting(true)
    router.delete(invitationPath({ id: invite!.id }), {
      preserveScroll: true,
      onSuccess: () => {
        toast.success(t("admin.invites.toast.revoked"))
        setRevokeOpen(false)
      },
      onError: () => {
        toast.error(t("admin.invites.errors.server"))
      },
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Card
      data-testid="admin-pending-first-admin-card"
      data-tenant-id={tenantId}
      data-card-state="pending"
    >
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2 text-base font-medium">
          <Mail className="text-muted-foreground size-4" aria-hidden="true" />
          {t("admin.invites.card.pending.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-foreground truncate text-sm font-medium">
            {invite.email}
          </span>
          <span className="text-muted-foreground text-xs">
            {t("admin.invites.card.pending.sent_at", {
              relative: formatRelativeIntl(
                invite.sent_at,
                toAppLocale(i18n.language),
              ),
            })}
            {" · "}
            {t("admin.invites.card.pending.expires_at", {
              relative: formatRelativeIntl(
                invite.expires_at,
                toAppLocale(i18n.language),
              ),
            })}
          </span>
        </div>
        {/*
         * Action group. UX Spec §Responsive Behaviour: stacked full-width
         * on <640px; row layout sm+. Badge sits at the top of the stack on
         * mobile so the status reads first-thing; on sm+ it sits inline
         * to the left of the action buttons.
         */}
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-nowrap sm:items-center">
          <StatusBadge
            status={invite.status}
            className="self-start sm:self-center"
          />
          <ResendButton
            email={invite.email}
            disabled={submitting || !online}
            offline={!online}
            offlineTooltip={t("admin.invites.card.pending.offline_tooltip")}
            onClick={handleResend}
          />
          <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
            <RevokeButton
              email={invite.email}
              disabled={submitting || !online}
              offline={!online}
              offlineTooltip={t("admin.invites.card.pending.offline_tooltip")}
            />
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("admin.invites.card.pending.revoke_confirm_title")}
                </AlertDialogTitle>
                {/*
                 * AlertDialogDescription is a `<p>` by default — only
                 * inline children are valid HTML inside. The action
                 * statement lives here; the email block renders OUTSIDE
                 * the Description so we can use a `<div className="block
                 * break-all">` without nesting block-level inside `<p>`.
                 * UX refinement: email on its own line with `break-all`,
                 * SR reads natively, no `title`-tooltip dependency.
                 */}
                <AlertDialogDescription>
                  {t("admin.invites.card.pending.revoke_confirm_body")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div
                className="text-foreground bg-muted/40 rounded-md border px-3 py-2 text-sm font-medium break-all"
                data-testid="admin-pending-first-admin-revoke-email"
              >
                {invite.email}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel autoFocus>
                  {t("admin.invites.card.pending.revoke_confirm_cancel")}
                </AlertDialogCancel>
                {/* OKS-1023 — plain `Button`: `AlertDialogAction` is Base UI
                    `Dialog.Close` and would close before the request settles. */}
                <Button
                  type="button"
                  className="bg-destructive hover:bg-destructive/90 text-white"
                  onClick={handleRevoke}
                  disabled={submitting || !online}
                  aria-disabled={submitting || !online ? "true" : undefined}
                  data-testid="admin-pending-first-admin-revoke-confirm"
                >
                  {t("admin.invites.card.pending.revoke_confirm_submit")}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Resend button — wraps the trigger in a `<Tooltip>` that surfaces an
 * "You're offline" hint when the button is disabled by network state.
 * Sighted users see why the button is greyed; SR users hear the same via
 * `aria-disabled` + the trigger's `aria-label`. Pattern mirrors the
 * `+N more` disabled tooltip on the digest card.
 */
function ResendButton({
  email,
  disabled,
  offline,
  offlineTooltip,
  onClick,
}: {
  email: string
  disabled: boolean
  offline: boolean
  offlineTooltip: string
  onClick: () => void
}) {
  const { t } = useTranslation()
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled ? "true" : undefined}
      aria-label={t("admin.invites.card.pending.resend_aria", { email })}
      data-testid="admin-pending-first-admin-resend"
    >
      <RotateCw className="size-4" aria-hidden="true" />
      {t("admin.invites.card.pending.resend")}
    </Button>
  )
  if (!offline) return button
  // When disabled-by-offline, wrap a span around the button so Radix's
  // Tooltip trigger still receives pointer + focus events (disabled
  // <button>s don't dispatch them in the DOM).
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span tabIndex={0} className="inline-flex">
            {button}
          </span>
        }
      />
      <TooltipContent>{offlineTooltip}</TooltipContent>
    </Tooltip>
  )
}

/**
 * Revoke trigger — same offline-tooltip pattern as `ResendButton`. Mounts
 * inside the `AlertDialog` provider context (the trigger asChild is the
 * Button itself; the wrapping `<span>` only appears when offline).
 */
function RevokeButton({
  email,
  disabled,
  offline,
  offlineTooltip,
}: {
  email: string
  disabled: boolean
  offline: boolean
  offlineTooltip: string
}) {
  const { t } = useTranslation()
  const button = (
    <AlertDialogTrigger
      render={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={disabled}
          aria-disabled={disabled ? "true" : undefined}
          aria-label={t("admin.invites.card.pending.revoke_aria", { email })}
          data-testid="admin-pending-first-admin-revoke-trigger"
        >
          <Trash2 className="size-4" aria-hidden="true" />
          {t("admin.invites.card.pending.revoke")}
        </Button>
      }
    />
  )
  if (!offline) return button
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span tabIndex={0} className="inline-flex">
            {button}
          </span>
        }
      />
      <TooltipContent>{offlineTooltip}</TooltipContent>
    </Tooltip>
  )
}
